import React from "react";

import { mdiChevronDown, mdiChevronRight, mdiLightningBolt } from "@mdi/js";
import Icon from "@mdi/react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { CostInfoBar } from "./EncodingConfigPanel";
import EncodingWarningModal from "./EncodingWarningModal";
import FormatBadge from "./FormatBadge";
import {
  getAggregateStatus,
  getFastStatus,
  getGlobalStatus,
} from "./OutputStatusPanel";
import useEncodingPanel from "../../hooks/useEncodingPanel";
import Block from "../block/Block";
import Button from "../button/Button";

import type { BadgeStatus } from "./FormatBadge";
import type {
  EncodingProfile,
  EncodingStream,
  PivotMedia,
  SourceMedia,
} from "../../api/EncoderAPI.types";
import type { FastAsset } from "../../api/MediaAPI.types";
import type { StartEncodingOutcome } from "../../hooks/useMediaEncoding";
import type { AppState } from "../../reducers";

interface EncodingPanelSimpleProps {
  sourceMedias: (SourceMedia | PivotMedia)[];
  encodingProfiles: EncodingProfile[];
  isProfilesLoading: boolean;
  isEncoding: boolean;
  isStartingEncoding: boolean;
  onStartEncoding: (profileUuid: string) => Promise<StartEncodingOutcome>;
  streams: EncodingStream[];
  fastAsset: FastAsset | null | undefined;
  onViewDetails: () => void;
}

export default function EncodingPanelSimple({
  sourceMedias,
  encodingProfiles,
  isProfilesLoading,
  isEncoding,
  isStartingEncoding,
  onStartEncoding,
  streams,
  fastAsset,
  onViewDetails,
}: EncodingPanelSimpleProps): React.JSX.Element {
  const { t } = useTranslation("editor-content");

  const fastPlan = useSelector(
    (state: AppState) =>
      state.fetchReducer.orga_config.data?.organization_options.fast_plan,
  );

  const {
    selectedProfileUuid,
    selectedProfile,
    setManualSelection,
    allQualitiesEncoded,
    resetAllQualitiesEncoded,
    canStartEncoding,
    startEncodingTooltip,
    quotas,
    handleEncodingClick,
    isOpenEncodingWarningModal,
    closeEncodingWarningModal,
    confirmEncoding,
  } = useEncodingPanel({
    sourceMedias,
    encodingProfiles,
    isEncoding,
    isStartingEncoding,
    onStartEncoding,
    fastAsset,
  });

  const videoStreamList = streams.filter((stream) => stream.type === "video");
  const audioStreamList = streams.filter((stream) => stream.type === "audio");
  const subtitleStreamList = streams.filter(
    (stream) => stream.type === "subtitles",
  );
  const chapterStreamList = streams.filter(
    (stream) => stream.type === "chapters",
  );
  const d2gStreamList = streams.filter((stream) => stream.type === "d2g");

  const videoStatus = getAggregateStatus(videoStreamList);
  const audioStatus = getAggregateStatus(audioStreamList);
  const subtitlesStatus = getAggregateStatus(subtitleStreamList);
  const chaptersStatus = getAggregateStatus(chapterStreamList);
  const d2gStatus = getAggregateStatus(d2gStreamList);
  const fastStatus = getFastStatus(fastAsset, fastPlan);

  const globalStatus = getGlobalStatus({
    video: videoStatus,
    audio: audioStatus,
    subtitles: subtitlesStatus,
    chapters: chaptersStatus,
    d2g: d2gStatus,
    fast: fastStatus,
  });

  const outputFormatList: {
    key: string;
    label: string;
    status: BadgeStatus;
  }[] = [
    { key: "video", label: t("video"), status: videoStatus },
    { key: "audio", label: t("audio"), status: audioStatus },
    { key: "subtitles", label: t("subtitles"), status: subtitlesStatus },
    { key: "chapters", label: t("chapters"), status: chaptersStatus },
    { key: "d2g", label: t("d2g"), status: d2gStatus },
    {
      key: "fast",
      label: `${t("fastAsset")} ${fastAsset?.framerate ? `${fastAsset.framerate} fps` : ""}`,
      status: fastStatus,
    },
  ].filter((format) => format.status !== "unavailable");

  const headerComponent = (
    <div className="flex flex-1 flex-wrap items-center justify-end gap-3">
      <FormatBadge
        withoutTooltip
        label={t(`streamStatus.${globalStatus}`)}
        status={globalStatus}
      />
      <Button
        withGlow
        data-tooltip-content={startEncodingTooltip}
        data-tooltip-id="start-encoding-tooltip"
        isDisabled={!canStartEncoding}
        onClick={handleEncodingClick}
        size="xs"
        type="button"
        data-tooltip-hidden={
          canStartEncoding ? !allQualitiesEncoded : undefined
        }
      >
        <Icon path={mdiLightningBolt} size={0.625} />
        {isEncoding ? t("encodingInProgress") : t("startEncoding")}
      </Button>
    </div>
  );

  return (
    <React.Fragment>
      <Block
        classNameHeader="h-[4.375rem]"
        headerComponent={headerComponent}
        title={t("encodingPanelSimpleTitle")}
      >
        <div className="flex flex-col gap-6">
          {isProfilesLoading ? (
            <p className="text-muted-foreground text-2xl">
              {t("loadingProfiles")}
            </p>
          ) : (
            <div className="relative">
              <select
                className="focus:border-primary border-border bg-card text-foreground w-full appearance-none rounded-lg border py-1.5 pr-6.25 pl-2.5 text-base focus:outline-none"
                id="encoding-profile-select-simple"
                value={selectedProfileUuid ?? ""}
                onChange={(e): void => {
                  setManualSelection(e.target.value);
                  resetAllQualitiesEncoded();
                }}
              >
                {encodingProfiles.map((profile) => (
                  <option key={profile.uuid} value={profile.uuid}>
                    {profile.label}
                  </option>
                ))}
              </select>
              <Icon
                className="text-muted-foreground pointer-events-none absolute top-1/2 right-4 -translate-y-1/2"
                path={mdiChevronDown}
                size={1}
              />
            </div>
          )}

          {selectedProfile ? (
            <CostInfoBar
              withLabels
              costFactor={selectedProfile.cost_factor}
              quotas={quotas}
              sourceMedias={sourceMedias}
            />
          ) : null}

          {outputFormatList.length > 0 && (
            <div className="flex flex-col gap-6">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-muted-foreground text-[0.78125rem] font-semibold">
                  {t("encodedOutputs")}
                </span>
                {outputFormatList.map((format) => (
                  <FormatBadge
                    key={format.key}
                    label={format.label}
                    status={format.status}
                  />
                ))}
              </div>
              <Button
                className="ml-auto"
                onClick={onViewDetails}
                size="xs"
                type="button"
                variant="ghost"
              >
                {t("viewDetails")}
                <Icon path={mdiChevronRight} size={0.625} />
              </Button>
            </div>
          )}
        </div>
      </Block>
      <EncodingWarningModal
        isOpen={isOpenEncodingWarningModal}
        onClose={closeEncodingWarningModal}
        onConfirm={confirmEncoding}
      />
    </React.Fragment>
  );
}
